type SaleContactApiItem = {
    data?: {
        saleContact?: {
            key?: string;
            title?: string;
            visible?: boolean;
            cta?: Array<{
                saleContactSection?: {
                    key?: string;
                    visible?: boolean;
                    content?: Array<{
                        headerSection?: {
                            key?: string;
                            title?: string;
                            visible?: boolean;
                            description?: string;
                            lists?: Array<{
                                id?: number | string;
                                label?: string;
                                title?: string;
                            }>;
                        };
                        breadCrumbSection?: {
                            key?: string;
                            visible?: boolean;
                            breadCrumbTitleNd?: string;
                            breadCrumbTitleSt?: string;
                        };
                    }>;
                };
            }>;
        };
    };
};

export type SaleContactVersionItem = {
    contentId: number;
    contentUuid: string;
    contentVersionUuid: string;
    version: number;
    isPublished: boolean;
    status: string;
    summary?: string | null;
    createdBy?: string;
    createdAt?: string;
    data?: {
        saleContact?: {
            key?: string;
            title?: string;
            visible?: boolean;
            cta?: Array<{
                saleContactSection?: {
                    key?: string;
                    visible?: boolean;
                    content?: Array<{
                        headerSection?: {
                            key?: string;
                            title?: string;
                            visible?: boolean;
                            description?: string;
                            lists?: Array<{
                                id?: number | string;
                                label?: string;
                                title?: string;
                            }>;
                        };
                        breadCrumbSection?: {
                            key?: string;
                            visible?: boolean;
                            breadCrumbTitleNd?: string;
                            breadCrumbTitleSt?: string;
                        };
                    }>;
                };
            }>;
        };
    };
};

export type NormalizedSaleContact = {
    page: "saleContact";
    title: string;
    visible: boolean;

    section: {
        visible: boolean;
    };

    header: {
        title: string;
        description: string;
        visible: boolean;
        lists: Array<{
            id: string;
            label: string;
            title: string;
        }>;
    };

    breadcrumb: {
        visible: boolean;
        first: string;
        second: string;
    };

    meta: {
        key: string;
    };
};

export function normalizeSaleContact(
    item: SaleContactApiItem
): NormalizedSaleContact | null {
    const saleContact = item?.data?.saleContact;
    const cta = saleContact?.cta?.[0];
    const section = cta?.saleContactSection;
    const content = section?.content?.[0];
    const header = content?.headerSection;
    const breadcrumb = content?.breadCrumbSection;

    if (!saleContact || !cta || !section || !content) return null;

    return {
        page: "saleContact",
        title: saleContact.title ?? "",
        visible: Boolean(saleContact.visible),

        section: {
            visible: Boolean(section.visible),
        },

        header: {
            title: header?.title ?? "",
            description: header?.description ?? "",
            visible: Boolean(header?.visible),
            lists: (header?.lists ?? []).map((entry) => ({
                id: String(entry.id ?? ""),
                label: entry.label ?? "",
                title: entry.title ?? "",
            })),
        },

        breadcrumb: {
            visible: Boolean(breadcrumb?.visible),
            first: breadcrumb?.breadCrumbTitleSt ?? "",
            second: breadcrumb?.breadCrumbTitleNd ?? "",
        },

        meta: {
            key: saleContact.key ?? "",
        },
    };
}

type SaleContactCompareResult = {
    changedPublishedOnly: Partial<NormalizedSaleContact>;
    changedDraftOnly: Partial<NormalizedSaleContact>;
    diff: Record<
        string,
        {
            published: unknown;
            draft: unknown;
        }
    >;
};

const isSame = (a: unknown, b: unknown) =>
    JSON.stringify(a) === JSON.stringify(b);

export function compareSaleContactChangedSections(
    published: NormalizedSaleContact,
    draft: NormalizedSaleContact
): SaleContactCompareResult {
    const changedPublishedOnly: Partial<NormalizedSaleContact> = {};
    const changedDraftOnly: Partial<NormalizedSaleContact> = {};
    const diff: Record<string, { published: unknown; draft: unknown }> = {};

    if (!isSame(published.title, draft.title)) {
        changedPublishedOnly.title = published.title;
        changedDraftOnly.title = draft.title;
        diff.title = {
            published: published.title,
            draft: draft.title,
        };
    }

    if (!isSame(published.visible, draft.visible)) {
        changedPublishedOnly.visible = published.visible;
        changedDraftOnly.visible = draft.visible;
        diff.visible = {
            published: published.visible,
            draft: draft.visible,
        };
    }

    if (!isSame(published.section, draft.section)) {
        changedPublishedOnly.section = published.section;
        changedDraftOnly.section = draft.section;
        diff.section = {
            published: published.section,
            draft: draft.section,
        };
    }

    if (!isSame(published.header, draft.header)) {
        changedPublishedOnly.header = published.header;
        changedDraftOnly.header = draft.header;
        diff.header = {
            published: published.header,
            draft: draft.header,
        };
    }

    if (!isSame(published.breadcrumb, draft.breadcrumb)) {
        changedPublishedOnly.breadcrumb = published.breadcrumb;
        changedDraftOnly.breadcrumb = draft.breadcrumb;
        diff.breadcrumb = {
            published: published.breadcrumb,
            draft: draft.breadcrumb,
        };
    }

    if (!isSame(published.meta, draft.meta)) {
        changedPublishedOnly.meta = published.meta;
        changedDraftOnly.meta = draft.meta;
        diff.meta = {
            published: published.meta,
            draft: draft.meta,
        };
    }

    return {
        changedPublishedOnly,
        changedDraftOnly,
        diff,
    };
}

export function findPublishedAndDraftSaleContact(
    versions: SaleContactVersionItem[],
    selectedVersionUuid: string
) {
    const draftItem = versions.find(
        (item) => item.contentVersionUuid === selectedVersionUuid
    );

    if (!draftItem) {
        return {
            draftItem: null,
            publishedItem: null,
        };
    }

    const publishedItem = versions.find(
        (item) =>
            item.contentId === draftItem.contentId &&
            item.isPublished === true
    );

    return {
        draftItem,
        publishedItem: publishedItem ?? null,
    };
}